'use client';
// ──────────────────────────────────────────────────────
// AiCubeHero — Premium AI-powered animated centerpiece
//
// Homepage idle-screen hero element. Replaces the static
// scan-ring icon with a 3D-inspired rotating cube surrounded
// by orbital elements and pulsing AI glow.
//
// Q / Cube brand identity: geometric, structured, futuristic.
// ──────────────────────────────────────────────────────
import { motion, useReducedMotion } from 'framer-motion';

const SIZE = 160;
const HALF = SIZE / 2;
const CUBE = 52;

export function AiCubeHero() {
    const prefersReduced = useReducedMotion();

    return (
        <div
            style={{
                position: 'relative',
                width: SIZE,
                height: SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {/* Ambient glow */}
            <motion.div
                animate={prefersReduced ? {} : { scale: [1, 1.15, 1], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute',
                    inset: -20,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(14,165,165,0.3) 0%, rgba(14,165,165,0.08) 55%, transparent 100%)',
                    filter: 'blur(18px)',
                }}
            />

            {/* Orbital ring 1 — slow */}
            <motion.div
                animate={prefersReduced ? {} : { rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
                style={{
                    position: 'absolute',
                    width: SIZE - 10,
                    height: SIZE - 10,
                    borderRadius: '50%',
                    border: '1.5px solid rgba(14,165,165,0.15)',
                }}
            >
                {/* Orbiting dot */}
                <motion.div
                    style={{
                        position: 'absolute',
                        top: -4,
                        left: '50%',
                        marginLeft: -4,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#0ea5a5',
                        boxShadow: '0 0 10px 2px rgba(14,165,165,0.5)',
                    }}
                />
            </motion.div>

            {/* Orbital ring 2 — tilted, faster */}
            <motion.div
                animate={prefersReduced ? {} : { rotate: -360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                style={{
                    position: 'absolute',
                    width: SIZE - 30,
                    height: SIZE - 30,
                    borderRadius: '50%',
                    border: '1px solid rgba(14,165,165,0.12)',
                    transform: 'rotateX(60deg)',
                }}
            >
                <motion.div
                    style={{
                        position: 'absolute',
                        bottom: -3,
                        left: '50%',
                        marginLeft: -3,
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: 'rgba(14,165,165,0.7)',
                        boxShadow: '0 0 8px 2px rgba(14,165,165,0.4)',
                    }}
                />
            </motion.div>

            {/* Scanning sweep line */}
            {!prefersReduced && (
                <motion.div
                    animate={{ y: [-(HALF - 20), HALF - 20, -(HALF - 20)] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute',
                        width: CUBE + 20,
                        height: 2,
                        borderRadius: 1,
                        background: 'linear-gradient(90deg, transparent, rgba(14,165,165,0.6), transparent)',
                        filter: 'blur(0.5px)',
                    }}
                />
            )}

            {/* Cube container — 3D rotation */}
            <motion.div
                animate={prefersReduced ? {} : {
                    rotateY: [0, 90, 180, 270, 360],
                    rotateX: [0, 15, 0, -15, 0],
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: 'easeInOut',
                }}
                style={{
                    width: CUBE,
                    height: CUBE,
                    position: 'relative',
                    transformStyle: 'preserve-3d',
                    perspective: 400,
                }}
            >
                {/* Cube faces */}
                {(['front', 'back', 'left', 'right', 'top', 'bottom'] as const).map((face) => {
                    const transforms: Record<string, string> = {
                        front:  `translateZ(${CUBE / 2}px)`,
                        back:   `rotateY(180deg) translateZ(${CUBE / 2}px)`,
                        left:   `rotateY(-90deg) translateZ(${CUBE / 2}px)`,
                        right:  `rotateY(90deg) translateZ(${CUBE / 2}px)`,
                        top:    `rotateX(90deg) translateZ(${CUBE / 2}px)`,
                        bottom: `rotateX(-90deg) translateZ(${CUBE / 2}px)`,
                    };
                    return (
                        <div
                            key={face}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                transform: transforms[face],
                                backfaceVisibility: 'hidden',
                                borderRadius: 8,
                                border: '1.5px solid rgba(14,165,165,0.35)',
                                background: 'linear-gradient(135deg, rgba(14,165,165,0.08) 0%, rgba(14,165,165,0.02) 100%)',
                                backdropFilter: 'blur(4px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {face === 'front' && (
                                <span style={{ fontSize: 22, fontWeight: 900, color: '#0ea5a5', letterSpacing: '-0.04em' }}>Q</span>
                            )}
                            {face === 'right' && (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ea5a5" strokeWidth="1.5" strokeLinecap="round">
                                    <circle cx="12" cy="12" r="3" />
                                    <path d="M12 1v4M12 19v4M1 12h4M19 12h4" />
                                </svg>
                            )}
                            {face === 'back' && (
                                <span style={{ fontSize: 14, color: '#0ea5a5', opacity: 0.7 }}>AI</span>
                            )}
                            {face === 'left' && (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0ea5a5" strokeWidth="1.5" strokeLinecap="round">
                                    <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
                                    <line x1="7" y1="12" x2="17" y2="12" />
                                </svg>
                            )}
                        </div>
                    );
                })}
            </motion.div>

            {/* Corner accent dots */}
            {[0, 1, 2, 3].map((i) => (
                <motion.div
                    key={`corner-${i}`}
                    animate={prefersReduced ? {} : {
                        opacity: [0.3, 0.8, 0.3],
                        scale: [0.8, 1.1, 0.8],
                    }}
                    transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: i * 0.6,
                    }}
                    style={{
                        position: 'absolute',
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: '#0ea5a5',
                        top: i < 2 ? 12 : undefined,
                        bottom: i >= 2 ? 12 : undefined,
                        left: i % 2 === 0 ? 12 : undefined,
                        right: i % 2 !== 0 ? 12 : undefined,
                    }}
                />
            ))}
        </div>
    );
}
